import { useState, useContext } from 'react';
import { Modal, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import tableStyle from '../Style/TableModel.module.css';
import formStyle from '../Style/modalForm.module.css';
import button from '../Style/ButtonStyle.module.css';
import AuthContext from "../../store/auth-context";
import { fetchEpubUsers, migrateEpubUsers } from '../../services/user-migration.service';

interface ImportUsersProps {
    modalStatus: boolean;
    onAction: () => void;
    organisationDetail: any;
    onImportComplete: () => void;
}

const ImportUsers: React.FC<ImportUsersProps> = (props) => {
    const authCtx = useContext(AuthContext);
    const [isLoading, setIsLoading] = useState(false);
    const [epubUsers, setEpubUsers] = useState<any[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
    const [showUserList, setShowUserList] = useState(false);
    const [isMigrating, setIsMigrating] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const handleFetchUsers = async () => {
        if (!props.organisationDetail?.epubOrgId) {
            toast.error("ePublisher Organization ID not found");
            return;
        }

        setIsLoading(true);
        setEpubUsers([]);
        setShowUserList(false);
        setErrorMessage('');

        try {
            const response = await fetchEpubUsers(
                props.organisationDetail.epubOrgId,
                authCtx.token
            );

            if (response.error || !response.data) {
                const errorMsg = response.message || "No users found in ePublisher database";
                setErrorMessage(errorMsg);
                toast.error(errorMsg);
            } else {
                setEpubUsers(response.data);
                setShowUserList(true);
                // Select all users by default
                const allUserEmails = new Set(response.data.map((u: any) => u.user_email_id));
                setSelectedUsers(allUserEmails);
                toast.success(`Found ${response.data.length} users`);
            }
        } catch (error: any) {
            console.error("Error fetching users:", error);
            const errorMsg = error.message || "Failed to fetch users from  ePublisher";
            setErrorMessage(errorMsg);
            toast.error(errorMsg);
        } finally {
            setIsLoading(false);
        }
    };

    const handleToggleUser = (userEmail: string) => {
        const newSelected = new Set(selectedUsers);
        if (newSelected.has(userEmail)) {
            newSelected.delete(userEmail);
        } else {
            newSelected.add(userEmail);
        }
        setSelectedUsers(newSelected);
    };

    const handleToggleAll = () => {
        if (selectedUsers.size === epubUsers.length) {
            setSelectedUsers(new Set());
        } else {
            const allUserEmails = new Set(epubUsers.map((u: any) => u.user_email_id));
            setSelectedUsers(allUserEmails);
        }
    };

    const handleMigrateUsers = async () => {
        if (selectedUsers.size === 0) {
            toast.error("Please select at least one user to import");
            return;
        }

        const usersToMigrate = epubUsers.filter(u => selectedUsers.has(u.user_email_id));

        setIsMigrating(true);

        try {
            const response = await migrateEpubUsers(
                usersToMigrate,
                props.organisationDetail.uuid,
                authCtx.token
            );

            if (response.status === 200 && response.summary) {
                toast.success(
                    `Migration completed: ${response.summary.success} created, ${response.summary.skipped} skipped, ${response.summary.failed} failed`
                );
                
                if (response.results) {
                    if (response.results.failed.length > 0) {
                        console.error("Failed users:", response.results.failed);
                    }
                    if (response.results.skipped.length > 0) {
                        console.info("Skipped users:", response.results.skipped);
                    }
                }

                props.onImportComplete();
                handleCloseModal();
            } else {
                toast.error("User migration failed");
            }
        } catch (error: any) {
            console.error("Error migrating users:", error);
            toast.error(error.message || "Failed to migrate users");
        } finally {
            setIsMigrating(false);
        }
    };

    const handleCloseModal = () => {
        setEpubUsers([]);
        setSelectedUsers(new Set());
        setShowUserList(false);
        setErrorMessage('');
        props.onAction();
    };

    return (
        <>
            <Modal
                size="xl"
                show={props.modalStatus}
                onHide={handleCloseModal}
                aria-labelledby="import-users-modal-title"
            >
                <div className={formStyle.headArea}>
                    <Modal.Title id="import-users-modal-title">
                        <h4 className={tableStyle.modalTitle}>Import Admin Users from ePublisher</h4><br/>
                        <p style={{ fontSize: '14px', color: '#666', marginTop: '5px' }}>
                            Organization: {props.organisationDetail?.name}
                        </p>
                    </Modal.Title>
                </div>

                <div className={formStyle.bodyArea}>
                    {!showUserList ? (
                        <div className="row">
                            <div className="col-12">
                                <p>Click the button below to fetch users from ePublisher database for this organization.</p>
                                <div className="mb-3">
                                    <button
                                        onClick={handleFetchUsers}
                                        className={button.btn + ' ' + button.blueButton}
                                        disabled={isLoading}
                                    >
                                        {isLoading ? 'Fetching Users...' : 'Fetch Users from  ePublisher '}
                                    </button>
                                </div>
                                {errorMessage && (
                                    <div className="alert alert-danger mt-3">
                                        {errorMessage}
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="row">
                            <div className="col-12">
                                <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <p style={{ margin: 0 }}>
                                        Found {epubUsers.length} users. Selected: {selectedUsers.size}
                                    </p>
                                    <button
                                        onClick={handleToggleAll}
                                        className={button.smBtn + ' ' + button.lightButton}
                                    >
                                        {selectedUsers.size === epubUsers.length ? 'Deselect All' : 'Select All'}
                                    </button>
                                </div>
                                <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                    <Table striped bordered hover size="sm">
                                        <thead style={{ position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 1 }}>
                                            <tr>
                                                <th style={{ width: '50px' }}>
                                                    <input
                                                        type="checkbox"
                                                        checked={selectedUsers.size === epubUsers.length}
                                                        onChange={handleToggleAll}
                                                    />
                                                </th>
                                                <th>First Name</th>
                                                <th>Last Name</th>
                                                <th>Email</th>
                                                <th>Phone</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {epubUsers.map((user, index) => (
                                                <tr key={index}>
                                                    <td>
                                                        <input
                                                            type="checkbox"
                                                            checked={selectedUsers.has(user.user_email_id)}
                                                            onChange={() => handleToggleUser(user.user_email_id)}
                                                        />
                                                    </td>
                                                    <td>{user.user_first_name || 'N/A'}</td>
                                                    <td>{user.user_last_name || 'N/A'}</td>
                                                    <td>{user.user_email_id}</td>
                                                    <td>{user.user_phone || 'N/A'}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                                <div style={{ marginTop: '15px' }}>
                                    <div className="alert alert-info">
                                        <strong>Note:</strong> Users will be created with status "INACTIVE" and random passwords. 
                                        They will need to use the password reset feature to set their own passwords.
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                <div className={formStyle.FooterArea}>
                    <button
                        type="button"
                        onClick={handleCloseModal}
                        className={button.btn + ' ' + button.brownButton}
                        disabled={isMigrating}
                    >
                        Cancel
                    </button>
                    {showUserList && (
                        <button
                            type="button"
                            onClick={handleMigrateUsers}
                            className={button.btn + ' ' + button.blueButton + ' ' + button.buttonSpaceLeft}
                            disabled={isMigrating || selectedUsers.size === 0}
                        >
                            {isMigrating ? 'Importing...' : `Import ${selectedUsers.size} User(s)`}
                        </button>
                    )}
                </div>
            </Modal>
        </>
    );
};

export default ImportUsers;
